import {
  AmountInputField,
  FormDivider,
  MonthlyPayment,
  Partnership,
  PolarQuestion,
  PrimaryApplicantIncome,
  SearchableSelectField,
  SecondaryApplicantIncome,
  SecondaryApplicantInquiry,
} from '@hex/calculators-shared/components';
import { LTV_THRESHOLD , featureFlags } from '@hex/calculators-shared/constants';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { InterestRatesResponse } from '@hex/calculators-shared/types';
import { AffordabilityFormDataType } from '@hex/calculators-shared/types/affordability';
import { municipalityOptions } from '@hex/shared/common';
import { Location, Sack } from '@sapphire/icons/react';
import { Button, Flex, Icon, SelectItem, tokens, HelpButton, Typography } from '@sapphire-react/core';
import { MutableRefObject, RefObject, useEffect } from 'react';
import { type UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AffordabilityResponse } from '../../types';
import { AddLoanCredit } from './AddLoanCredit';
import { AddProperty } from './AddProperty';
import { HouseholdSection } from './HouseholdSection';
import  { PropertyTypeField } from './PropertyTypeField';
import { useAffordabilityForm } from './useAffordabilityForm.hook';

type Props = {
  buttonRef: RefObject<any>;
  submitRef: MutableRefObject<any>;
  interestResponseData: InterestRatesResponse| number | null;
  affordabilityResponseData: AffordabilityResponse | null;
  loading?: boolean;
  onFormChange: () => void;
  onSubmit: (data: AffordabilityFormDataType, isDirty: boolean, reset: UseFormReset<AffordabilityFormDataType>) => void;
};

export function Form({
  onSubmit,
  interestResponseData,
  affordabilityResponseData,
  onFormChange,
  buttonRef,
  submitRef,
  loading,
}: Props) {
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const { t } = useTranslation(['affordabilityCalculator', 'shared']);
  const inputSize = isExtraSmall || isSmall ? 'md' : 'lg';

  const {
    control,
    formFields,
    handleSubmit,
    reset,
    watch,
    propertyTypeRules,
    municipalityRules,
    monthlyPaymentRules,
    downpaymentRules,
    isDirty,
    trackCalculatorStarted,
  } = useAffordabilityForm();

  // Resets results
  useEffect(() => {
    if (isDirty && interestResponseData && affordabilityResponseData) {
      onFormChange();
    }
  }, [isDirty, onFormChange, interestResponseData, affordabilityResponseData]);

  const monthlyPayment = watch('monthlyPayment');

  // Sets the submitref so that in case of an error, the form can be submitted again from ErrorMessage
  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit(() => onSubmit(formFields, isDirty, reset));
    }
  }, [formFields, handleSubmit, isDirty, onSubmit, reset, submitRef]);

  return (
    <S.Form
      onSubmit={handleSubmit((formData) => onSubmit(formData, isDirty, reset))}
      autoComplete={'off'}
      onClick={trackCalculatorStarted}
    >
      <Flex flexDirection={'column'} gap={tokens.size.spacingMd}>
        <PropertyTypeField control={control} rules={propertyTypeRules} />
        <SearchableSelectField
          control={control}
          name={'municipality'}
          data-test-id={'municipality'}
          rules={municipalityRules}
          placeholder={t('personalInformation.inputs.municipality.placeholder')}
          label={t('personalInformation.inputs.municipality.label')}
          width={'100%'}
          prefix={
            <Icon size={inputSize}>
              <Location />
            </Icon>
          }
        >
          {municipalityOptions.map((item) => (
            <SelectItem key={item.value}>{item.label}</SelectItem>
          ))}
        </SearchableSelectField>
        <MonthlyPayment
          control={control}
          name={'monthlyPayment'}
          label={t('personalInformation.inputs.estimatedExpensesPayment.label')}
          rules={monthlyPaymentRules}
          fieldFormValue={monthlyPayment}
          contextualHelp={
            <HelpButton
              variant={'help'}
              aria-label={'Hjälp!'}
              contentBody={
                <>
                  <Typography.Body size={'sm'}>
                    <strong>{t('personalInformation.inputs.estimatedExpensesPayment.tooltip.payment.title')}:</strong>{' '}
                    {t('personalInformation.inputs.estimatedExpensesPayment.tooltip.payment.description')}
                  </Typography.Body>
                  <Typography.Body marginTop={tokens.size.spacing2xs} size={'sm'}>
                    <strong>{t('personalInformation.inputs.estimatedExpensesPayment.tooltip.expenses.title')}:</strong>{' '}
                    {t('personalInformation.inputs.estimatedExpensesPayment.tooltip.expenses.description')}
                  </Typography.Body>
                </>
              }
            />
          }
        />
        <AmountInputField
          control={control}
          name={'downpayment'}
          label={t('personalInformation.inputs.downPayment.label')}
          rules={downpaymentRules}
          width={'100%'}
          postfix={'kr'}
          min={0}
          max={100_000_000}
          prefix={
            <Icon size={inputSize}>
              <Sack />
            </Icon>
          }
          contextualHelp={
            <HelpButton
              variant={'help'}
              aria-label={'Hjälp!'}
              contentBody={t('personalInformation.inputs.downPayment.tooltip', {
                downpaymentAmount: 100 - LTV_THRESHOLD,
              })}
            />
          }
        />
        <FormDivider />
        <SecondaryApplicantInquiry control={control} />
        <PrimaryApplicantIncome control={control} fieldFormValue={formFields.primaryApplicantIncome} />
        {formFields.secondaryApplicantExist === PolarQuestion.Yes && (
          <SecondaryApplicantIncome control={control} fieldFormValue={formFields.secondaryApplicantIncome} />
        )}
        {!featureFlags.use3MonthInterestRate && (
          <Partnership
            control={control}
            withSecondaryApplicant={formFields.secondaryApplicantExist === PolarQuestion.Yes}
          />
        )}
        <FormDivider />
        <HouseholdSection control={control} />
        <FormDivider marginBottom={0} marginTop={8} />
        <AddProperty control={control} watch={watch} />
        <FormDivider marginBottom={0} marginTop={0} />
        <AddLoanCredit control={control} watch={watch} />
        <FormDivider marginBottom={0} marginTop={0} />
      </Flex>
      <Flex justifyContent={'center'} marginTop={tokens.size.spacing2xl}>
        <Button ref={buttonRef} type={'submit'} isDisabled={loading} name={'Beräkna'}>
          {loading ? t('shared:loading') : t('shared:calculate')}
        </Button>
      </Flex>
    </S.Form>
  );
}

const S = {
  Form: styled.form`
    display: flex;
    flex-direction: column;
  `,
};
